import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthLayout } from '@/components/layout/auth-layout';
import { useAuthStore } from '@/lib/auth-store';
import type { ApiError } from '@/lib/api-client';
import { useBranding } from '@/lib/use-branding';

// ─── Paleta idêntica à landing ──────────────────────────────────
const CHAMPAGNE = '#E8B96A';
const CHAMPAGNE_LIGHT = '#F0C674';
const FG = '#F5F1FA';
const MUTED = '#A99FB8';
const SURFACE = 'rgba(27,16,41,0.85)';
const BORDER = 'rgba(255,255,255,0.09)';

// ─── Estilos base dos inputs ─────────────────────────────────────
const inputBase: React.CSSProperties = {
  width: '100%',
  background: 'rgba(10,7,16,0.6)',
  border: `1px solid ${BORDER}`,
  borderRadius: 8,
  padding: '11px 14px',
  fontFamily: "'Hanken Grotesk', sans-serif",
  fontSize: 14.5,
  color: FG,
  outline: 'none',
  boxSizing: 'border-box',
  transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
};

// ─── Utilitários (lógica de autenticação — NÃO alterar) ─────────
function isSafeRedirect(target: string | null): target is string {
  if (!target) return false;
  return target.startsWith('/') && !target.startsWith('//');
}

function extractApiMessage(err: unknown, fallback: string): string {
  const body = (err as { body?: unknown })?.body;
  const raw = (body as { message?: unknown })?.message;
  if (typeof raw === 'string') return raw;
  return (err as Error)?.message ?? fallback;
}

// ────────────────────────────────────────────────────────────────
export default function SignInPage() {
  // ── Estado e lógica de autenticação (INTOCÁVEIS) ──────────────
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const signIn = useAuthStore((s) => s.signIn);
  const { branding } = useBranding();

  const redirectParam =
    searchParams.get('redirect') ?? searchParams.get('next');
  const safeRedirect = isSafeRedirect(redirectParam) ? redirectParam : null;
  const msg = searchParams.get('msg');
  const signupDisabled = msg === 'signup-disabled';
  const passwordResetOk = msg === 'password-reset-ok';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await signIn(email, password);
      if (res.mfaRequired) {
        navigate('/mfa-verify');
        return;
      }
      // Decisao de destino. authStore atualiza isSuperAdmin/tenantId.
      const state = useAuthStore.getState();
      if (safeRedirect) {
        navigate(safeRedirect);
      } else if (state.isSuperAdmin) {
        navigate('/admin');
      } else if (!state.tenantId) {
        navigate('/sem-escritorio');
      } else {
        navigate('/escritorio');
      }
    } catch (err) {
      const status = (err as ApiError | undefined)?.status;
      if (status === 401 || status === 400) {
        setError('Credenciais inválidas');
      } else {
        setError(extractApiMessage(err, 'Falha ao entrar'));
      }
    } finally {
      setLoading(false);
    }
  }
  // ── Fim da lógica de autenticação ─────────────────────────────

  const showLoginLogo = branding?.brandingEnabled && branding.loginLogoUrl;

  return (
    <AuthLayout>
      {/* Card de login */}
      <div
        style={{
          width: '100%',
          background: SURFACE,
          border: '1px solid rgba(232,185,106,0.15)',
          borderRadius: 18,
          padding: 'clamp(28px, 5vw, 44px)',
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          boxShadow:
            '0 0 80px rgba(109,40,217,0.15), 0 2px 48px rgba(0,0,0,0.4)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Glow sutil atrás do card */}
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: -60,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 380,
            height: 260,
            background:
              'radial-gradient(ellipse at center, rgba(109,40,217,0.18) 0%, transparent 70%)',
            pointerEvents: 'none',
            filter: 'blur(2px)',
          }}
        />

        <div style={{ position: 'relative' }}>
          {/* Logo customizado do white-label (quando configurado) */}
          {showLoginLogo && (
            <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'center' }}>
              <img
                src={branding.loginLogoUrl!}
                alt={branding.headerName ?? 'Logo'}
                className="logo-shadow-light"
                style={{ height: branding.loginLogoSizePx, objectFit: 'contain' }}
              />
            </div>
          )}

          {/* Cabeçalho */}
          <h2
            style={{
              fontFamily: "'Fraunces', Georgia, serif",
              fontWeight: 400,
              fontSize: 'clamp(22px, 3.5vw, 28px)',
              letterSpacing: '-0.01em',
              color: FG,
              margin: '0 0 6px',
            }}
          >
            Entrar no{' '}
            <span style={{ color: CHAMPAGNE }}>
              {branding?.headerName ?? 'VoxisAdv'}
            </span>
          </h2>
          <p
            style={{
              fontFamily: "'Hanken Grotesk', sans-serif",
              fontSize: 14,
              color: MUTED,
              margin: '0 0 28px',
            }}
          >
            Use seu e-mail e senha cadastrados.
          </p>

          {/* Avisos de contexto (estado de URL) */}
          {signupDisabled && (
            <div
              role="status"
              style={{
                background: 'rgba(234,179,8,0.08)',
                border: '1px solid rgba(234,179,8,0.22)',
                borderRadius: 8,
                padding: '11px 14px',
                fontFamily: "'Hanken Grotesk', sans-serif",
                fontSize: 13,
                color: '#FDE68A',
                marginBottom: 20,
              }}
            >
              Cadastros desabilitados — solicite acesso ao administrador.
            </div>
          )}
          {passwordResetOk && (
            <div
              role="status"
              style={{
                background: 'rgba(74,222,128,0.08)',
                border: '1px solid rgba(74,222,128,0.22)',
                borderRadius: 8,
                padding: '11px 14px',
                fontFamily: "'Hanken Grotesk', sans-serif",
                fontSize: 13,
                color: '#86EFAC',
                marginBottom: 20,
              }}
            >
              Senha redefinida com sucesso. Entre com sua nova senha.
            </div>
          )}

          {/* Formulário */}
          <form onSubmit={handleSubmit} noValidate>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {/* Campo e-mail */}
              <div>
                <label
                  htmlFor="email"
                  style={{
                    display: 'block',
                    fontFamily: "'Hanken Grotesk', sans-serif",
                    fontSize: 13,
                    fontWeight: 500,
                    color: MUTED,
                    marginBottom: 6,
                    letterSpacing: '0.01em',
                  }}
                >
                  E-mail
                </label>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  style={inputBase}
                  onFocus={(e) => {
                    (e.currentTarget as HTMLInputElement).style.borderColor =
                      'rgba(168,85,247,0.5)';
                    (e.currentTarget as HTMLInputElement).style.boxShadow =
                      '0 0 0 3px rgba(109,40,217,0.12)';
                  }}
                  onBlur={(e) => {
                    (e.currentTarget as HTMLInputElement).style.borderColor =
                      BORDER;
                    (e.currentTarget as HTMLInputElement).style.boxShadow =
                      'none';
                  }}
                />
              </div>

              {/* Campo senha */}
              <div>
                <label
                  htmlFor="password"
                  style={{
                    display: 'block',
                    fontFamily: "'Hanken Grotesk', sans-serif",
                    fontSize: 13,
                    fontWeight: 500,
                    color: MUTED,
                    marginBottom: 6,
                    letterSpacing: '0.01em',
                  }}
                >
                  Senha
                </label>
                <input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  style={inputBase}
                  onFocus={(e) => {
                    (e.currentTarget as HTMLInputElement).style.borderColor =
                      'rgba(168,85,247,0.5)';
                    (e.currentTarget as HTMLInputElement).style.boxShadow =
                      '0 0 0 3px rgba(109,40,217,0.12)';
                  }}
                  onBlur={(e) => {
                    (e.currentTarget as HTMLInputElement).style.borderColor =
                      BORDER;
                    (e.currentTarget as HTMLInputElement).style.boxShadow =
                      'none';
                  }}
                />
              </div>

              {/* Erro de autenticação */}
              {error && (
                <p
                  role="alert"
                  style={{
                    fontFamily: "'Hanken Grotesk', sans-serif",
                    fontSize: 13,
                    color: '#FCA5A5',
                    margin: 0,
                    background: 'rgba(239,68,68,0.08)',
                    border: '1px solid rgba(239,68,68,0.2)',
                    borderRadius: 8,
                    padding: '10px 14px',
                  }}
                >
                  {error}
                </p>
              )}

              {/* Esqueci a senha */}
              <div style={{ textAlign: 'right', marginTop: -4 }}>
                <Link
                  to="/forgot-password"
                  style={{
                    fontFamily: "'Hanken Grotesk', sans-serif",
                    fontSize: 13,
                    color: MUTED,
                    textDecoration: 'none',
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLAnchorElement).style.color = FG;
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLAnchorElement).style.color = MUTED;
                  }}
                >
                  Esqueceu a senha?
                </Link>
              </div>

              {/* Botão de submit */}
              <button
                type="submit"
                disabled={loading}
                style={{
                  marginTop: 4,
                  background: loading
                    ? 'rgba(232,185,106,0.4)'
                    : `linear-gradient(135deg, ${CHAMPAGNE} 0%, ${CHAMPAGNE_LIGHT} 100%)`,
                  color: '#1A0F00',
                  fontFamily: "'Hanken Grotesk', sans-serif",
                  fontWeight: 700,
                  fontSize: 15,
                  letterSpacing: '0.01em',
                  border: 'none',
                  borderRadius: 10,
                  padding: '15px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  width: '100%',
                  boxShadow: `0 4px 24px rgba(232,185,106,0.25)`,
                  transition: 'opacity 0.15s ease, transform 0.15s ease',
                }}
                onMouseEnter={(e) => {
                  if (!loading) {
                    (e.currentTarget as HTMLButtonElement).style.transform =
                      'translateY(-1px)';
                    (e.currentTarget as HTMLButtonElement).style.boxShadow =
                      '0 6px 28px rgba(232,185,106,0.35)';
                  }
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.transform =
                    'translateY(0)';
                  (e.currentTarget as HTMLButtonElement).style.boxShadow =
                    '0 4px 24px rgba(232,185,106,0.25)';
                }}
              >
                {loading ? 'Entrando...' : 'Entrar'}
              </button>
            </div>
          </form>

          {/* Link de cadastro */}
          <p
            style={{
              fontFamily: "'Hanken Grotesk', sans-serif",
              fontSize: 13,
              color: MUTED,
              textAlign: 'center',
              marginTop: 20,
            }}
          >
            Não tem conta?{' '}
            <Link
              to="/sign-up"
              style={{
                color: FG,
                textDecoration: 'none',
                fontWeight: 500,
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.color = CHAMPAGNE;
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.color = FG;
              }}
            >
              Criar conta
            </Link>
          </p>
        </div>
      </div>
    </AuthLayout>
  );
}
